import { useMemo, useState } from 'react';
import { Text } from '@bendhealth/common';
import styled from '@emotion/styled';
import { Patient } from 'api/account/patients.type';
import { FooterButton } from 'components/structure/footer/FooterButton';
import { FooterPortal } from 'components/structure/footer/FooterPortal';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { useCreateLeadFromMember } from 'hooks/useCreateLeadFromMember';
import { useNavigate } from 'react-router-dom';

import { useNpLeadsPerMember } from '../../../api/account/useNpLeadsPerMember';
import OnboardingTitle from '../components/OnboardingTitle';
import { ChooseCoreMemberLine } from './choose_member/ChooseCoreMemberLine';
import { ChooseMemberLine } from './choose_member/ChooseMemberLine';
import { ChooseMemberSubText } from './choose_member/ChooseMemberSubText';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

interface ChooseMemberProps {
  title: string;
  type: 'core' | 'np';
  handleNewMember?: () => void;
  handleNextClick?: (member: Patient) => void;
}

const ChooseLines = {
  core: ChooseCoreMemberLine,
  np: ChooseMemberLine,
};

const ChooseMember = ({ title, type, handleNewMember, handleNextClick }: ChooseMemberProps) => {
  const [selectedOption, setSelectedOption] = useState('');
  const npLeads = useNpLeadsPerMember();
  const user = useCurrentUser();
  const createLeadFromMember = useCreateLeadFromMember();

  const memberWithoutMyself = useMemo(() => {
    return user.state?.members
      ?.filter((member) => !member.personalAccountId)
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
  }, [user.state?.members]);

  const ChooseLine = ChooseLines[type];

  const myselfId = user.state?.members?.[0]?.id;

  const handleNext = () => {
    if (selectedOption === 'new-member') {
      handleNewMember?.();
      return;
    } else {
      const member = user.state?.members?.find((m) => m.id == selectedOption);
      if (!member) {
        return;
      }
      handleNextClick?.(member);
    }
  };
  return (
    <>
      <OnboardingTitle
        title={title}
        subTitle={
          <>
            <Text variant="body-4">Select one for now:</Text>{' '}
            <ChooseMemberSubText>(You can enroll others later)</ChooseMemberSubText>
          </>
        }
      />
      <Content>
        {memberWithoutMyself?.map((member) => {
          return (
            <ChooseLine
              member={member}
              key={member.id}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          );
        })}

        <ChooseLine
          member={{
            id: 'new-member',
            firstName: 'Other Family Member',
            unfinishedCoreOnboarding: false,
            unfinishedNpOnboarding: false,
          }}
          subText={'(ages 2-25)'}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />

        {user.isSelf && myselfId && !npLeads[myselfId] && (
          <ChooseLine
            member={{
              id: myselfId,
              firstName: 'Myself',
              unfinishedNpOnboarding: false,
              unfinishedCoreOnboarding: false,
            }}
            subText={'(ages 2-25)'}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        )}
      </Content>
      <FooterPortal>
        <FooterButton
          disabled={selectedOption === '' || createLeadFromMember.isLoading}
          isLoading={createLeadFromMember.isLoading}
          onClick={handleNext}
        >
          Next
        </FooterButton>
      </FooterPortal>
    </>
  );
};

export default ChooseMember;
